/* eslint-disable max-lines */
"use client";
import PropTypes from "prop-types";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import React, { useCallback, useEffect, useMemo, useRef } from "react";

// local component imports
import Feed from "./dynamicHomeComponent";
import OneTapLogin from "../../components/organisms/googleOneTapLogin";
import MastHead from "./dynamicHomeComponent/GenericComponents/MastHead";
import PlpSkeleton from "../../components/molecules/skeletons/plpSkeleton";
import CommonConditionalComponents from "../../components/molecules/commonConditionalComponents";
import HomeSkeleton from "../../components/molecules/skeletons/homeSkeleton";
// services and utils imports
import useAnalytics from "../../analytics/useAnalytics";
import { COUNTRIES } from "../../constants/common/common";
import useNavigationReset from "../../customHooks/useNavigationReset";
import { loadAccuracyWeightageOnIdle } from "../../api/common";
import useHomeCampaignFeed from "../../customHooks/useHomeCampaignFeed";
import CookieUtil, { setCookieIfAbsent } from "../../utils/storage/cookieUtil";
import { setCatalogId, setIsMobile } from "../../store/slices/common/appConfig";
import { getPageTypeFromURL, PageTypes, setPageType } from "../../utils/fnpPageType";
import { cleverTapCommonEvents, cleverTapCommonWords } from "../../analytics/clevertapConstants";
import { useABTestingView } from "../../customHooks/clevertap/useABTestingView";
import {
  HDFC_SMARTBUY,
  LAST_VISITED_ADDRESS,
  LAST_VISITED_INTERNATIONAL_LOCATION,
  LAST_VISITED_PINCODE,
  PARTNER,
} from "../../constants/storage";

// slick css imports
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import LottieLoader from "../../components/atoms/lottieLoader";
import { buildPartnerMembershipFlags } from "../../utils/common";

/**
 * Returns the pathname of `document.referrer` for analytics internal_source fields.
 * @returns {string}
 */
function getDocumentReferrerPathname() {
  if (typeof document === "undefined") {
    return "";
  }
  try {
    const { referrer } = document;
    return referrer ? new URL(referrer).pathname : "";
  } catch {
    return "";
  }
}

/**
 * HomePage component renders the main content of the homepage.
 * @component
 * @param {object} props - The component props.
 * @param {string} props.catalogId - The catalog identifier.
 * @param {boolean} props.isMobile - Whether the device is mobile.
 * @param {object} props.serverPageData - Server-side page data. For desktop: `{ widgets }`. For mobile: `{ stores: [{ feed: { widgets } }] }`.
 * @param {object} props.timeData - Same-day delivery timing data.
 * @param {object} props.ratingAndReviewCount - Rating and review count data.
 * @param {object} props.productsAttrTagResponse - Product attribute tag response.
 * @param {object} props.freeBannerData - Free banner data.
 * @param {object} props.userUUID - for unique fnp id
 * @returns {JSX.Element} The rendered homepage content.
 */
const DynamicHomePage = ({
  isMobile,
  serverPageData,
  userUUID,
  catalogId,
  freeBannerData,
  currencyListWithHeaders,
  // Props wired in future iterations:
  // catalogId, timeData, ratingAndReviewCount, productsAttrTagResponse, isSmartBuyUser
}) => {
  useEffect(() => loadAccuracyWeightageOnIdle(), []);
  const dispatch = useDispatch();
  const isNavigating = useNavigationReset();
  const { init, trackEvent } = useAnalytics();
  const { recallABTestingHook } = useABTestingView();
  const { loginDetail, emailID, pageStoresRedux, dekstopReduxWidgets, isLoading } = useSelector(
    (state) => ({
      loginDetail: state?.fus?.loginDetail,
      emailID: state?.loginSignUp?.userDetails?.emailID,
      pageStoresRedux: state?.dynamicHome?.pageStores,
      dekstopReduxWidgets: state?.dynamicHome?.desktopWidgets,
      isLoading: state?.dynamicHome?.isLoading,
    }),
    shallowEqual,
  );
  const previousLoginStatusRef = useRef(loginDetail?.loggedin);
  const lastVisitedPincode = CookieUtil.getCookie(LAST_VISITED_PINCODE);
  const lastVisitedAddress = CookieUtil.getCookie(LAST_VISITED_ADDRESS);
  const selectedIntlCity = CookieUtil.getCookie(LAST_VISITED_INTERNATIONAL_LOCATION);
  const isSmartBuyUser = CookieUtil?.getCookie(PARTNER) === HDFC_SMARTBUY;
  const pathname = typeof window !== "undefined" && window.location.pathname ? window.location.pathname : "";
  const completePathName = typeof window !== "undefined" && window.location.href ? window.location.href : "";

  let mastHeadData = [];
  let selectedStore = null;
  let widgets = [];

  if (isMobile) {
    // ----- Mobile -----
    // pageStores in Redux is the single source of truth.
    // The active store is identified by store.isSelected === true (set by selectMobileStore reducer).
    mastHeadData = pageStoresRedux.length > 0 ? pageStoresRedux : (serverPageData?.stores ?? []);
    selectedStore = mastHeadData.find((s) => s.isSelected);
    widgets = selectedStore?.feed?.widgets ?? [];
  } else {
    // ----- Desktop -----
    // TODO: desktop SSR data shape is still in flux — swap to Redux-first once saga is wired
    const desktopSSRWidgets = serverPageData?.widgets ?? [];
    widgets = dekstopReduxWidgets?.length > 0 ? dekstopReduxWidgets : desktopSSRWidgets;
  }

  useEffect(() => {
    init();
    setCookieIfAbsent("ab_user_id", userUUID);
    setPageType(PageTypes.HOME);
    dispatch(setIsMobile(isMobile));
    dispatch(setCatalogId(catalogId));
  }, []);

  const isCampaignLoading = useHomeCampaignFeed({ isMobile, serverPageData, userUUID });
  const duplicateEventData = useMemo(() => {
    const indiaPincode = catalogId !== COUNTRIES.INDIA ? null : lastVisitedPincode;
    return {
      complete_url: completePathName,
      country: catalogId,
      city: catalogId !== COUNTRIES.INDIA ? selectedIntlCity : null,
      area: catalogId !== COUNTRIES.INDIA ? null : lastVisitedAddress,
      pincode: indiaPincode,
      system_pincode: indiaPincode,
    };
  }, [catalogId, completePathName, lastVisitedAddress, lastVisitedPincode, selectedIntlCity]);

  const homePageViewedBase = useMemo(
    () => ({
      clean_url: pathname,
      page_name: cleverTapCommonWords.HOME,
      page_type: cleverTapCommonWords.HOME,
      login_status: loginDetail?.loggedin,
      is_international: catalogId !== COUNTRIES.INDIA,
      is_smartbuy_user: isSmartBuyUser,
      store_name: selectedStore?.storeName,
      ...duplicateEventData,
    }),
    [catalogId, duplicateEventData, isSmartBuyUser, loginDetail?.loggedin, pathname],
  );

  const storeViewedData = useMemo(
    () => ({
      store_name: selectedStore?.storeName,
      pincode: duplicateEventData?.pincode,
      city: duplicateEventData?.city,
    }),
    [duplicateEventData?.city, duplicateEventData?.pincode, selectedStore?.storeName],
  );

  const buildHomePageViewedPayload = useCallback(() => {
    const internalSourcePath = getDocumentReferrerPathname();
    return {
      ...homePageViewedBase,
      internal_source_type: getPageTypeFromURL(internalSourcePath),
      internal_source: internalSourcePath,
    };
  }, [homePageViewedBase]);

  useEffect(() => {
    const loginStatusChanged = previousLoginStatusRef.current !== loginDetail?.loggedin;
    const homePageViewedData = buildHomePageViewedPayload();

    if (loginStatusChanged) {
      if (loginDetail?.loggedin !== undefined) {
        trackEvent(
          cleverTapCommonEvents?.homePageViewed,
          { ...homePageViewedData, ...buildPartnerMembershipFlags(loginDetail?.partner) },
          ["clevertap"],
        );
      }

      recallABTestingHook?.();
      previousLoginStatusRef.current = loginDetail?.loggedin;
    }

    if (loginDetail?.loggedin && emailID) {
      trackEvent(cleverTapCommonEvents?.homePageViewed, { ...homePageViewedData, user_id: emailID }, ["CDP"]);
    } else if (loginDetail?.loggedin === false && !emailID) {
      trackEvent(cleverTapCommonEvents?.homePageViewed, { ...homePageViewedData, user_id: "" }, ["CDP"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginDetail?.loggedin, emailID]);

  useEffect(() => {
    if (storeViewedData?.store_name && isMobile) {
      trackEvent(cleverTapCommonEvents?.storeViewed, storeViewedData, ["clevertap"]);
      trackEvent(cleverTapCommonEvents?.storeViewed, storeViewedData, ["CDP"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore?.storeId, isMobile]);

  return (
    <>
      {/* <CursorManager />
      {occasionSpecialEffect && <OccasionSpecialEffects isMobile={isMobile} />} */}
      <OneTapLogin />
      {isCampaignLoading && isMobile && (
        <LottieLoader isShowLottie={false} position="fixed" zIndex="z-[999]" backdropBlur="15px" />
      )}

      {isNavigating && (
        <div className="fixed inset-0 z-[9999] mt-[60px] bg-white-900 md:mt-[50px]">
          <PlpSkeleton
            showHeader={true}
            showFilters={true}
            productCount={12}
            isMobile={isMobile}
            isHomePage={true}
            hasBanner={catalogId === COUNTRIES.INDIA && !isMobile && Boolean(freeBannerData?.desktop)}
          />
        </div>
      )}
      {isMobile ? <MastHead mastHeadData={mastHeadData} currencyListWithHeaders={currencyListWithHeaders} /> : null}
      <div className={isMobile ? "w-full overflow-x-hidden" : ""}>
        {isMobile && isLoading ? (
          <HomeSkeleton />
        ) : widgets?.length > 0 ? (
          <div className={`w-full ${isMobile ? "animate-fadeIn" : ""}`}>
            <Feed widgets={widgets} isMobile={isMobile} isCampaignLoading={isCampaignLoading} />
          </div>
        ) : null}
      </div>
      <CommonConditionalComponents isMobile={isMobile} />
    </>
  );
};

export default React.memo(DynamicHomePage);

DynamicHomePage.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  userUUID: PropTypes.string,
  serverPageData: PropTypes.shape({
    // Desktop shape
    widgets: PropTypes.array,
    // Mobile shape
    stores: PropTypes.arrayOf(
      PropTypes.shape({
        storeId: PropTypes.string,
        feed: PropTypes.shape({
          widgets: PropTypes.array,
        }),
      }),
    ),
  }),
  catalogId: PropTypes.string.isRequired,
  freeBannerData: PropTypes.object,
  currencyListWithHeaders: PropTypes.object,
};
